import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import { setData, disconnect } from '../../../features/ecBindingSlice';
import TextField from '../../shared/TextField';
import Tip from '../../shared/Tip';
import { color } from '../../../app/theme';
import IconDelete from '../../../assets/images/Icons/IconDelete';
import { openModal, closeModal } from '../../../features/modalSlice';
import { ModalType } from '../../../app/types';

const useStyles = makeStyles(
  {
    root: {},
    header: {
      fontSize: '17px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      color: color.$13,
      margin: '0 0 64px 0',
    },
    text: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      color: color.$11,
      marginBottom: 12,
      maxWidth: 676,
      '& > a': {
        color: color.$2,
      },
    },
    title: {
      fontSize: '20px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      color: color.$3,
      marginBottom: 12,
    },
    input: {
      width: 676,
    },
    sun: {
      marginTop: 44,
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$7,
      background: color.$15,
      width: 80,
      borderRadius: '20px',
      justifyContent: 'center',
      padding: '2px 0',
      marginBottom: 6,
    },
    hint: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
    },
    stop: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      color: color.$10,
      cursor: 'pointer',
      '& > svg': {
        marginRight: 4,
      },
    },
    tip: {
      maxWidth: 698,
    },
    hr: {
      backgroundColor: color.$9,
      height: 1,
      width: '100%',
      margin: '24px 0',
    },
    subtitle: {
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '18px',
      color: color.$11,
      marginTop: 6,
      '& > a': {
        color: color.$2,
        textDecoration: 'underline',
      },
    },
  },
  {
    name: 'TokenContainer',
  },
);

const TokenContainer = ({ provider }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const { data, isSaved } = useSelector(store => store.ecBinding);
  const { credential, merchant } = data || {};
  const { token, webhookKey, webhookSecret } = credential || {};
  const { domain } = merchant || {};

  const handleKeywordChange = e => {
    dispatch(
      setData({
        credential: {
          ...credential,
          token: e?.target?.value,
        },
      }),
    );
  };

  const handleWebhookKeyChange = e => {
    dispatch(
      setData({
        credential: {
          ...credential,
          webhookKey: e?.target?.value,
        },
      }),
    );
  };

  const handleWebhookSecretChange = e => {
    dispatch(
      setData({
        credential: {
          ...credential,
          webhookSecret: e?.target?.value,
        },
      }),
    );
  };

  const handleDomainUrlChange = e => {
    dispatch(
      setData({
        merchant: {
          ...merchant,
          domain: e?.target?.value,
        },
      }),
    );
  };

  const handleStop = () => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`ecBinding:${provider}-disconnectTitle`),
          content: t(`ecBinding:${provider}-disconnectSubtitle`),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: () => {
            dispatch(disconnect(orgId, provider));
            dispatch(closeModal());
          },
          cancelText: t('modal.cancel'),
          submitText: t('modal.sure'),
        },
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {t(`ecBinding:${provider}-token`)}
        {provider === 'shopify' && (
          <div className={classes.subtitle}>
            {t(`ecBinding:${provider}-subtitle`)}
            <a
              href={t(`ecBinding:${provider}-token-helpUrl`)}
              rel="noopener noreferrer"
              target="_blank"
              className="link"
            >
              {t('mitake:help')}
            </a>
          </div>
        )}
      </div>
      {(provider === 'shopify' || provider === 'shopline') && (
        <>
          <div className={classes.text}>{t('ecBinding:token-step1')}</div>
          <div className={classes.title}>
            {t('ecBinding:token-step1-title')}
          </div>
          <div className={classes.text}>
            {t('ecBinding:token-step1-subtitle-1')}
            <a
              href={t(`ecBinding:${provider}Url`)}
              rel="noopener noreferrer"
              target="_blank"
              className="link"
            >
              {t(`ecBinding:${provider}Url-text`)}
            </a>
            {t(`ecBinding:${provider}-token-step1-subtitle-2`)}
          </div>
          <TextField
            className={classes.input}
            type="text"
            isValid
            value={token || ''}
            placeholder={t('ecBinding:token-placeholder')}
            onChange={handleKeywordChange}
            isError={isSaved && !token}
          />
        </>
      )}
      {provider === 'shopify' && (
        <>
          <div className={classes.title} style={{ marginTop: 24 }}>
            {t('ecBinding:webhookKeyTitle')}
          </div>
          <div className={classes.text}>
            {t('ecBinding:webhookKeySubtitle')}
          </div>
          <TextField
            style={{ marginBottom: 12 }}
            className={classes.input}
            type="text"
            isValid
            value={webhookKey || ''}
            placeholder={t('ecBinding:webhookKey-placeholder')}
            onChange={handleWebhookKeyChange}
            isError={isSaved && !webhookKey}
          />
          <TextField
            className={classes.input}
            type="text"
            isValid
            value={webhookSecret || ''}
            placeholder={t('ecBinding:webhookSecret-placeholder')}
            onChange={handleWebhookSecretChange}
            isError={isSaved && !webhookSecret}
          />
          <hr className={classes.hr} />
          <div className={classes.text}>{t('ecBinding:token-step2')}</div>
          <div className={classes.title}>
            {t('ecBinding:token-step2-title')}
          </div>
          <div className={classes.text}>
            {t('ecBinding:token-step2-subtitle')}
          </div>
          <TextField
            className={classes.input}
            type="text"
            isValid
            value={domain || ''}
            placeholder={t('ecBinding:domainUrl-placeholder')}
            onChange={handleDomainUrlChange}
            isError={isSaved && !domain}
          />
        </>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 12,
          marginBottom: 44,
        }}
      >
        <div className={classes.text} style={{ margin: 0 }}>
          {t(`ecBinding:${provider}-token-text3`)}
        </div>
        <div className={classes.stop} onClick={handleStop}>
          <IconDelete
            id="stop-connect"
            width={20}
            height={20}
            color={color.$10}
            hoverColor={color.$10}
          />
          {t(`ecBinding:${provider}-stop-connect`)}
        </div>
      </div>
      {provider === 'shopline' && (
        <Tip
          className={classes.tip}
          text={t(`ecBinding:${provider}-token-hint`)}
        />
      )}
    </div>
  );
};

export default TokenContainer;
